import { useState } from "react"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { Check } from "lucide-react"
import { toast } from "sonner"
import type { CampaignDetail, UpdateCampaignInput } from "@vienna/contracts"
import { CAMPAIGN_STATUS_LABELS } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Header, Section, SelectField, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { BackLink, EstimateCard, Footer, ProviderIcon, Steps } from "@/components/campaigns/primitives"
import { api, ApiError } from "@/lib/api"
import { campaignEstimateQuery, campaignQuery, channelsQuery, segmentsQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/campaigns/$campaignId/audience")({
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(segmentsQuery(params.workspace)), context.queryClient.ensureQueryData(channelsQuery(params.workspace))]),
  component: CampaignAudience,
})

export function useCampaignUpdate(campaignId: string) {
  const { workspace } = useWorkspaceContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateCampaignInput) => api<CampaignDetail>(`/w/${workspace.slug}/campaigns/${campaignId}`, { method: "PATCH", body: JSON.stringify(input) }),
    onSuccess: async (detail) => {
      queryClient.setQueryData(campaignQuery(workspace.slug, campaignId).queryKey, detail)
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "campaigns", campaignId, "estimate"] })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not save the campaign"),
  })
}

/** Screen 25. */
function CampaignAudience() {
  const { workspace } = useWorkspaceContext()
  const { campaignId } = Route.useParams()
  const navigate = useNavigate()
  const campaign = useSuspenseQuery(campaignQuery(workspace.slug, campaignId)).data
  const segments = useSuspenseQuery(segmentsQuery(workspace.slug)).data
  const connections = useSuspenseQuery(channelsQuery(workspace.slug)).data.connections.filter((c) => c.status !== "disconnected")
  const ready = Boolean(campaign.segmentId && campaign.connectionId)
  const estimate = useQuery({ ...campaignEstimateQuery(workspace.slug, campaignId), enabled: ready })
  const [name, setName] = useState(campaign.name)
  const update = useCampaignUpdate(campaignId)
  const params = { workspace: workspace.slug, campaignId }
  const editable = campaign.status === "draft"
  const segment = segments.find((s) => s.id === campaign.segmentId)
  return (
    <div className="page-wrap au-root">
      <Header title={campaign.name || "New campaign"} back={<BackLink to="/w/$workspace/campaigns" params={{ workspace: workspace.slug }}>Campaigns</BackLink>}>
        <Status>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Status>
      </Header>
      <Steps current="audience" workspace={workspace.slug} campaignId={campaignId} />
      <div className="au-two-column">
        <Section title="Choose your audience" description="Start with the people you want to reach.">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Campaign name</Label>
              <Input id="campaign-name" value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== campaign.name && update.mutate({ name: name.trim() })} />
            </div>
            <div className="space-y-2">
              <Label>Audience segment</Label>
              {segments.length ? (
                <SelectField label="Audience segment" value={campaign.segmentId ?? ""} onChange={(segmentId) => update.mutate({ segmentId: segmentId || null })} options={[{ value: "", label: "Choose a segment" }, ...segments.map((s) => ({ value: s.id, label: s.name }))]} className="w-full" />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No saved segments yet.{" "}
                  <Link className="underline underline-offset-4" to="/w/$workspace/contacts/segments/new" params={{ workspace: workspace.slug }}>
                    Create one
                  </Link>{" "}
                  to choose an audience.
                </p>
              )}
              {segment && (
                <>
                  <div className="flex justify-between gap-3 text-xs text-muted-foreground">
                    <span>{(segment.matchCount ?? 0).toLocaleString()} people match this segment</span>
                    <Link className="text-foreground underline underline-offset-4" to="/w/$workspace/contacts/segments/$segmentId" params={{ workspace: workspace.slug, segmentId: segment.id }}>
                      Edit conditions
                    </Link>
                  </div>
                  {campaign.segmentSummary && (
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      {campaign.segmentSummary.split(/\s(?:\+|·)\s/).map((part) => (
                        <Badge variant="secondary" className="h-auto whitespace-normal text-left" key={part}>
                          {part.replace(/^\(|\)$/g, "")}
                        </Badge>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <Separator />
            <div className="space-y-3">
              <Label>Delivery channel</Label>
              {connections.map((c) => {
                const selected = c.id === campaign.connectionId
                return (
                  <button key={c.id} className={selected ? "au-channel-selected" : "au-channel-selected opacity-70"} aria-pressed={selected} disabled={!editable} onClick={() => update.mutate({ connectionId: c.id })}>
                    <ProviderIcon provider={c.provider} className="size-5" />
                    <span className="flex-1 text-left">
                      <span className="block font-medium">{providerLabel(c.provider)}</span>
                      <span className="text-xs text-muted-foreground">{c.handle ?? c.displayName}</span>
                    </span>
                    {selected && <Check className="size-4" />}
                  </button>
                )
              })}
              {!connections.length && (
                <p className="text-sm text-muted-foreground">
                  No channel connected.{" "}
                  <Link className="underline underline-offset-4" to="/w/$workspace/channels/new" params={{ workspace: workspace.slug }} search={{}}>
                    Connect one
                  </Link>
                  .
                </p>
              )}
              <p className="text-xs text-muted-foreground">One channel per campaign. Only eligible identities on that channel are included.</p>
            </div>
          </div>
        </Section>
        <EstimateCard estimate={estimate.data} provider={campaign.provider} connectionLabel={campaign.connectionLabel} ready={ready} />
      </div>
      <Footer
        back={<BackLink to="/w/$workspace/campaigns" params={{ workspace: workspace.slug }}>Back</BackLink>}
        label="Continue to message"
        disabled={!name.trim() || !ready || !editable}
        pending={update.isPending}
        onNext={async () => {
          if (name.trim() !== campaign.name) await update.mutateAsync({ name: name.trim() })
          await navigate({ to: "/w/$workspace/campaigns/$campaignId/message", params })
        }}
      />
    </div>
  )
}
